import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { MapVersion, State, GuideLayer } from "../types";
import { convertStateToFirestore, convertFirestoreToState } from "./socialService";

// Save a version snapshot before updating
export const saveMapVersion = async (
  mapId: string,
  mapData: {
    name: string;
    description?: string;
    states: State[];
    guideLayers?: GuideLayer[];
    version: number;
    userId: string;
    userDisplayName: string;
    thumbnail?: string;
    isApproved?: boolean;
    approvedAt?: string;
    approvedBy?: string;
  },
  changeDescription?: string
): Promise<void> => {
  try {
    const versionsRef = collection(db, "maps", mapId, "versions");

    // Convert states to Firestore format for storage
    const firestoreStates = mapData.states.map(convertStateToFirestore);
    
    // Build version data, filtering out undefined values for Firestore
    const firestoreVersionData: any = {
      mapId,
      version: mapData.version,
      name: mapData.name,
      createdAt: new Date().toISOString(),
      createdBy: mapData.userId,
      createdByDisplayName: mapData.userDisplayName,
      states: firestoreStates,
      guideLayers: mapData.guideLayers || [],
    };

    // Only add optional fields if they have values (not undefined)
    if (mapData.description !== undefined) {
      firestoreVersionData.description = mapData.description;
    }
    if (changeDescription !== undefined) {
      firestoreVersionData.changeDescription = changeDescription;
    }
    if (mapData.isApproved !== undefined) {
      firestoreVersionData.isApproved = mapData.isApproved;
    }
    if (mapData.approvedAt !== undefined) {
      firestoreVersionData.approvedAt = mapData.approvedAt;
    }
    if (mapData.approvedBy !== undefined) {
      firestoreVersionData.approvedBy = mapData.approvedBy;
    }
    if (mapData.thumbnail !== undefined) {
      firestoreVersionData.thumbnail = mapData.thumbnail;
    }

    await addDoc(versionsRef, firestoreVersionData);
  } catch (error) {
    console.error("Error saving map version:", error);
    throw error;
  }
};

// Get all versions for a map
export const getMapVersions = async (mapId: string): Promise<MapVersion[]> => {
  try {
    const versionsRef = collection(db, "maps", mapId, "versions");
    const q = query(versionsRef, orderBy("version", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        mapId: data.mapId,
        version: data.version,
        name: data.name,
        description: data.description,
        states: (data.states || []).map(convertFirestoreToState),
        guideLayers: data.guideLayers || [],
        createdAt: data.createdAt,
        createdBy: data.createdBy,
        createdByDisplayName: data.createdByDisplayName,
        changeDescription: data.changeDescription,
        isApproved: data.isApproved,
        approvedAt: data.approvedAt,
        approvedBy: data.approvedBy,
        thumbnail: data.thumbnail,
      } as MapVersion;
    });
  } catch (error) {
    console.error("Error getting map versions:", error);
    throw error;
  }
};

// Get a specific version by version number
export const getMapVersion = async (
  mapId: string,
  version: number
): Promise<MapVersion | null> => {
  try {
    const versionsRef = collection(db, "maps", mapId, "versions");
    const q = query(versionsRef, where("version", "==", version), limit(1));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      mapId: data.mapId,
      version: data.version,
      name: data.name,
      description: data.description,
      states: (data.states || []).map(convertFirestoreToState),
      guideLayers: data.guideLayers || [],
      createdAt: data.createdAt,
      createdBy: data.createdBy,
      createdByDisplayName: data.createdByDisplayName,
      changeDescription: data.changeDescription,
      isApproved: data.isApproved,
      approvedAt: data.approvedAt,
      approvedBy: data.approvedBy,
      thumbnail: data.thumbnail,
    } as MapVersion;
  } catch (error) {
    console.error("Error getting map version:", error);
    throw error;
  }
};

// Helper function to normalize a state for comparison (only compare meaningful properties)
// Note: We don't compare IDs because they may be regenerated when restoring versions
const normalizeStateForComparison = (state: any) => {
  // Normalize coordinates for comparison (handle floating point precision and order)
  const normalizeCoordinates = (coords: any): string => {
    if (!coords) return '';
    return JSON.stringify(
      coords.map((coord: any) => 
        Array.isArray(coord) ? coord.map((c: any) => 
          Array.isArray(c) ? c.map((n: any) => typeof n === 'number' ? n.toFixed(6) : n).join(',') : c
        ).sort().join('|') : coord
      ).sort()
    );
  };

  return {
    name: state.name || '',
    coordinatesStr: normalizeCoordinates(state.coordinates),
    color: state.color || '',
    capital: state.capital || '',
  };
};

// Calculate diff between two versions
export const getVersionDiff = (
  oldVersion: MapVersion,
  newVersion: MapVersion
): {
  statesAdded: number;
  statesRemoved: number;
  statesModified: number;
  guideLayersAdded: number;
  guideLayersRemoved: number;
  nameChanged: boolean;
  descriptionChanged: boolean;
  thumbnailChanged: boolean;
} => {
  const oldStateIds = new Set(oldVersion.states.map((s) => s.id));
  const newStateIds = new Set(newVersion.states.map((s) => s.id));

  const statesAdded = newVersion.states.filter((s) => !oldStateIds.has(s.id)).length;
  const statesRemoved = oldVersion.states.filter((s) => !newStateIds.has(s.id)).length;
  
  // Only count as modified if the normalized state properties actually changed
  // Match states by ID first, then compare content
  const statesModified = newVersion.states.filter((newState) => {
    // Only check states that exist in the old version (same ID)
    if (!oldStateIds.has(newState.id)) return false;
    
    const oldState = oldVersion.states.find((os) => os.id === newState.id);
    if (!oldState) return false;
    
    // Compare normalized content (excluding ID and raw coordinates)
    const normalizedNew = normalizeStateForComparison(newState);
    const normalizedOld = normalizeStateForComparison(oldState);
    
    // Only return true if the meaningful content actually changed
    return JSON.stringify(normalizedNew) !== JSON.stringify(normalizedOld);
  }).length;

  const oldGuideLayerIds = new Set((oldVersion.guideLayers || []).map((g) => g.id));
  const newGuideLayerIds = new Set((newVersion.guideLayers || []).map((g) => g.id));

  const guideLayersAdded = (newVersion.guideLayers || []).filter(
    (g) => !oldGuideLayerIds.has(g.id)
  ).length;
  const guideLayersRemoved = (oldVersion.guideLayers || []).filter(
    (g) => !newGuideLayerIds.has(g.id)
  ).length;

  const nameChanged = oldVersion.name !== newVersion.name;
  const descriptionChanged = oldVersion.description !== newVersion.description;
  const thumbnailChanged = (oldVersion.thumbnail || '') !== (newVersion.thumbnail || '');

  return {
    statesAdded,
    statesRemoved,
    statesModified,
    guideLayersAdded,
    guideLayersRemoved,
    nameChanged,
    descriptionChanged,
    thumbnailChanged,
  };
};

